import React, { useEffect, useRef, useState } from 'react';
import { companyInfo } from '../mock';

const Footer = ({ onSectionClick }) => {
  const footerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (footerRef.current) observer.observe(footerRef.current);
    return () => observer.disconnect();
  }, []);

  const navLinks = [
    { label: 'Services', id: '#services' },
    { label: 'About',    id: '#about'    },
    { label: 'Process',  id: '#process'  },
    { label: 'FAQ',      id: '#faq'      },
    { label: 'Contact',  id: '#contact'  },
  ];

  const handleNav = (id) => {
    if (onSectionClick) {
      onSectionClick(id);
    } else {
      document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="py-10 px-6 border-t border-white/5 overflow-hidden" ref={footerRef}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .footer-animate {
          opacity: 0;
        }
        .footer-animate.visible {
          animation: fadeIn 0.8s ease-out forwards;
        }
      `}</style>
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
        {/* Logo / name */}
        <h2
          className={`footer-animate ${isVisible ? 'visible' : ''} text-base font-extrabold text-white/40`}
        >
          {companyInfo.name}
        </h2>

        {/* Nav links */}
        <nav
          className={`footer-animate ${isVisible ? 'visible' : ''} flex flex-wrap justify-center gap-6`}
          style={isVisible ? { animationDelay: '0.1s' } : {}}
          aria-label="Footer navigation"
        >
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => handleNav(link.id)}
              className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 hover:text-[#007bff] transition-colors duration-200"
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* Copyright & legal */}
        <p
          className={`footer-animate ${isVisible ? 'visible' : ''} text-gray-600 text-[10px] uppercase tracking-[0.2em]`}
          style={isVisible ? { animationDelay: '0.2s' } : {}}
        >
          © 2025 SGS Global Media. Scale Your Revenue.
        </p>
      </div>

      {/* NAP + Legal — crawlable by search engines */}
      <div
        className={`footer-animate ${isVisible ? 'visible' : ''} max-w-7xl mx-auto mt-8 pt-6 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4`}
        style={isVisible ? { animationDelay: '0.3s' } : {}}
      >
        <address className="not-italic text-gray-600 text-xs text-center md:text-left leading-relaxed">
          <span className="block">{companyInfo.name}</span>
          <span className="block">{companyInfo.address}</span>
          <a
            href={`mailto:${companyInfo.email}`}
            className="hover:text-[#007bff] transition-colors duration-200"
          >
            {companyInfo.email}
          </a>
        </address>
        <nav aria-label="Legal links" className="flex gap-6">
          <a
            href="/privacy-policy"
            className="text-xs text-gray-600 hover:text-[#007bff] transition-colors duration-200 uppercase tracking-[0.15em]"
          >
            Privacy Policy
          </a>
          <a
            href="/terms-of-service"
            className="text-xs text-gray-600 hover:text-[#007bff] transition-colors duration-200 uppercase tracking-[0.15em]"
          >
            Terms of Service
          </a>
        </nav>
      </div>
    </footer>
  );
};

export default Footer;
